import { AddCircleOutline, CloseCircleOutline } from '@aslanpay/portal-common-lib';
import { Box, Button, Flex, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  HIGHLIGHT_LIMIT,
  HIGHLIGHT_MAX,
  SortableHighlightRowProps,
} from './WhatsIncludedFields.types';

function SortableHighlightRow({ field, index, onRemove }: SortableHighlightRowProps) {
  const {
    register,
    watch,
    trigger,
    formState: { errors, touchedFields },
  } = useFormContext();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const value = watch(`highlights.${index}.text`) ?? '';

  const fieldError = (
    errors.highlights as Record<string, { text?: { message?: string } }> | undefined
  )?.[index]?.text;

  const fieldTouched = (
    touchedFields.highlights as Record<string, { text?: boolean }> | undefined
  )?.[index]?.text;

  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap="12px"
      transform={CSS.Transform.toString(transform)}
      transition={transition}
      opacity={isDragging ? 0.4 : 1}
    >
      <Text
        cursor="grab"
        userSelect="none"
        color="brand.darkGray"
        fontSize="16px"
        {...attributes}
        style={{ touchAction: 'none' }}
        {...listeners}
      >
        ⠿
      </Text>

      <Box flex={1}>
        <Box position="relative">
          <Input
            placeholder={`Highlight ${index + 1}`}
            maxLength={HIGHLIGHT_MAX}
            isInvalid={!!fieldError && !!fieldTouched}
            {...register(`highlights.${index}.text`, {
              onBlur: () => trigger(`highlights.${index}.text`),
            })}
          />
          <Text
            position="absolute"
            bottom="5px"
            right="5px"
            textStyle="extraExtraSmallMedium"
            color="brand.darkGray"
          >
            {value.length} / {HIGHLIGHT_MAX}
          </Text>
        </Box>

        {fieldError?.message && fieldTouched && (
          <Text textStyle="extraSmallRegular" color="system.error" mt="4px">
            {fieldError.message}
          </Text>
        )}
      </Box>

      <IconButton
        variant="link"
        aria-label="Remove highlight"
        icon={<CloseCircleOutline boxSize="24px" color="brand.purple" />}
        onClick={() => onRemove(index)}
      />
    </Flex>
  );
}

function WhatsIncludedFields() {
  const { control } = useFormContext();

  const { fields, append, remove, move } = useFieldArray({ control, name: 'highlights' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex((field) => field.id === active.id);
    const newIndex = fields.findIndex((field) => field.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    move(oldIndex, newIndex);
  };

  return (
    <Stack spacing="5px" mt="5px">
      {fields.length > 0 && (
        <Text textAlign="right" textStyle="smallMedium" color="brand.mutedPurple">
          {fields.length}/{HIGHLIGHT_LIMIT}
        </Text>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={fields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing="5px">
            {fields.map((field, index) => (
              <SortableHighlightRow key={field.id} field={field} index={index} onRemove={remove} />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Button
        alignSelf="flex-start"
        variant="link"
        leftIcon={<AddCircleOutline boxSize="20px" />}
        onClick={() => append({ text: '' })}
        isDisabled={fields.length >= HIGHLIGHT_LIMIT}
        mt="15px"
        p="0"
      >
        New highlight
      </Button>
    </Stack>
  );
}

export default WhatsIncludedFields;
